import { useCallback, useRef } from 'react';
import type React from 'react';

import type { CropperCanvas, CropperImage } from 'cropperjs';

import { useI18n } from '@change-corgi/core-react/i18n';
import { ClientRender } from '@change-corgi/core-react/ssr/render';

import type { CropperInstance, MediaCropArea } from '#src/app/shared/hooks/imageUpload';

import { useCropOnTransform } from './hooks/useCropOnTransform';
import { useCroppedCanvas } from './hooks/useCroppedCanvas';
import { useCropperKeyboard } from './hooks/useCropperKeyboard';
import { useImageReady } from './hooks/useImageReady';
import './registerCropperElements';

// The cropper element needs to be the same size as the uploader. To accomplish this,
// the same styling is passed to it.
const uploaderStyle = {
	top: 0,
	left: 0,
	bottom: 0,
	right: 0,
	position: 'absolute' as const,
};

const handleStyle = {
	top: 0,
	left: 0,
	right: 0,
	bottom: 0,
	position: 'absolute' as const,
};

type CropperElementProps = {
	crop?: MediaCropArea;
	disabled?: boolean;
	image?: string;
	// 100 is cropperjs's own hardcoded floor for each dimension, so these defaults leave
	// consumers that don't pass them with the exact behavior they had before the props existed.
	minContainerHeight?: number;
	minContainerWidth?: number;
	updateCrop: (imageCrop: MediaCropArea) => void;
	updateImage?: (blob: Blob) => void;
	onInitialized?: (instance: CropperInstance) => void;
};

const CropperElementInner: React.FC<CropperElementProps> = ({
	crop,
	disabled = false,
	image,
	minContainerHeight = 100,
	minContainerWidth = 100,
	updateCrop,
	updateImage,
	onInitialized,
}) => {
	const canvasRef = useRef<CropperCanvas | null>(null);
	const imageRef = useRef<CropperImage | null>(null);
	const { translate } = useI18n();

	const refForFocus = useCallback((node: HTMLDivElement | null) => {
		// this will trigger an autofocus on first render only
		node && node.focus();
	}, []);

	const handleKeyDown = useCropperKeyboard({ imageRef, disabled });
	const naturalSizeRef = useImageReady({ imageRef, image, crop });
	const getCroppedCanvas = useCroppedCanvas({ canvasRef, imageRef, image, onInitialized });
	useCropOnTransform({ canvasRef, imageRef, naturalSizeRef, getCroppedCanvas, updateCrop, updateImage });

	return (
		<div
			ref={refForFocus}
			// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- not fixed to avoid regression risk from an automated markup rewrite; needs manual accessibility review
			role="button"
			data-testid="cropper-container"
			tabIndex={0}
			aria-label={translate('corgi.sap.media.a11y.cropper.crop_aria_label')}
			onKeyDown={handleKeyDown}
			className="-ml-px h-full p-px focus:outline-2 focus:outline-offset-1 focus:outline-[hsla(305,6%,22%,0.185)]"
		>
			<cropper-canvas
				ref={canvasRef}
				disabled={disabled}
				style={{ ...uploaderStyle, minWidth: minContainerWidth, minHeight: minContainerHeight }}
			>
				<cropper-image ref={imageRef} src={image} alt="" initial-fit="cover" translatable data-qa="cropper-image" />
				<cropper-handle action="move" plain style={handleStyle} />
			</cropper-canvas>
		</div>
	);
};

/** cropperjs's custom elements aren't supported server-side (see registerCropperElements.ts). */
export const CropperElement: React.FC<CropperElementProps> = (props) => (
	<ClientRender>
		<CropperElementInner {...props} />
	</ClientRender>
);
